/** Facilitator-verified 24 October 2026; clinical/version owner reconfirms before ship.
 * Terminology only. No IDDSI descriptors, tests, diagrams, colours or logo. */
export const IDDSI_TERMINOLOGY = {
  frameworkVersion: '2.2',
  frameworkDate: 'April 2026',
  testingMethodsVersion: '2.0',
  attribution: 'IDDSI Framework v2.2 © IDDSI, CC BY-SA 4.0, https://iddsi.org/framework',
  guidance: 'https://iddsi.org/framework',
  frameworkDocuments: 'https://iddsi.org/resources/framework-documents',
  food: {
    '3': 'Liquidised',
    '4': 'Pureed',
    '5': 'Minced & Moist',
    '6': 'Soft & Bite-Sized',
    '7EC': 'Easy to Chew',
    '7': 'Regular',
  },
  fluid: {
    '0': 'Thin',
    '1': 'Slightly Thick',
    '2': 'Mildly Thick',
    '3': 'Moderately Thick',
    '4': 'Extremely Thick',
  },
} as const;
export const MEALTIME_OWNER_SCOPES = [
  'clinical_lead',
  'backup',
  'on_call',
  'governance',
  'iddsi',
  'retention',
  'kitchen_uat',
  'kitchen_access',
] as const;
export type MealtimeProjection = {
  latest?: { id: string; revision: number; hash: string } | null;
  timezone?: string;
  client_id: string;
  state: string;
  as_of: string;
  future_at: string | null;
  automatic_routing: boolean;
  /** Effective provider kill-switch state used by consumers and proof. */
  enabled?: boolean;
  fingerprint: string;
  manual_handoff: string;
  manual_contacts?: string;
  profile: null | {
    id: string;
    revision: number | null;
    hash: string | null;
    from: string | null;
    to: string | null;
    food: string | null;
    fluid: string | null;
    assistance: string | null;
    nutrition: string | null;
    review_at: string | null;
    expires_at: string | null;
    legacy_texture: string | null;
    legacy_fluid: string | null;
    legacy_allergy_unverified: string | null;
    diet_type?: string | null;
    intolerance_summary?: string | null;
    dietary_preference_summary?: string | null;
    supplement_plan?: string | null;
    review_due_date?: string | null;
    nutrition_risk_level?: string | null;
    nutrition_risk_flag?: boolean | null;
    provenance: Record<string, unknown> | null;
  };
  /** Baseline resident_diet_profiles projection retained for providers with G03 OFF. */
  baseline_profile?: null | {
    hash?: string | null;
    id?: string | null;
    texture_level?: string | null;
    fluid_consistency?: string | null;
    allergy_summary?: string | null;
    requires_meal_assistance?: boolean | null;
    nutrition_risk_level?: string | null;
    nutrition_risk_flag?: boolean | null;
    intolerance_summary?: string | null;
    dietary_preference_summary?: string | null;
    supplement_plan?: string | null;
    review_due_date?: string | null;
    diet_type?: string | null;
    effective_from?: string | null;
    effective_to?: string | null;
  };
  allergies: {
    id: string;
    allergen: string;
    status: string;
    reaction_type: string;
    severity: string;
    allergen_class: string[];
    updated_at: string | null;
  }[];
  allergy_revision: string;
  allergy_state: 'UNKNOWN' | 'CONFLICT' | 'RECORDED' | 'UNAVAILABLE';
};
export function mealtimeLevel(domain: 'food' | 'fluid', code: string | null | undefined): string {
  if (code === 'NBM') return 'Nil by mouth — authorised instruction';
  if (!code || code === 'UNRESOLVED') return 'Not confirmed — contact the responsible nurse';
  const levels: Record<string, string> = IDDSI_TERMINOLOGY[domain];
  const label = levels[code];
  return label
    ? `Level ${code === '7EC' ? '7' : code} ${label}`
    : 'Legacy terminology — confirm with the responsible nurse';
}
export function mealtimeCurrentness(
  p: MealtimeProjection,
  now = Date.now(),
  cached = false,
): string {
  const profile = p.profile;
  let state = p.state;
  if (profile?.to && Date.parse(profile.to) <= now) state = 'CURRENTNESS_UNKNOWN';
  else if (profile?.expires_at && Date.parse(profile.expires_at) <= now) state = 'INTERIM_EXPIRED';
  else if (
    profile?.review_at &&
    Date.parse(profile.review_at) <= now &&
    ['CURRENT_AUTHORISED', 'INTERIM'].includes(state)
  )
    state = 'REVIEW_OVERDUE';
  const labels: Record<string, string> = {
    CURRENT_AUTHORISED: 'Current authorised instruction',
    INTERIM: 'Interim instruction',
    INTERIM_EXPIRED: 'Interim authority expired — nurse review required',
    REVIEW_OVERDUE: 'Review overdue — last authorised instruction shown',
    SOURCE_RECONCILIATION_REQUIRED:
      'Clinical source changed — reconcile with the responsible nurse',
    LEGACY_UNVERIFIED: 'Legacy instruction — clinical acceptance unverified',
    NO_INSTRUCTION: 'No instruction recorded',
    READ_UNAVAILABLE: 'Read unavailable — confirm with the responsible nurse',
    CURRENTNESS_UNKNOWN: 'Currentness unknown — confirm with the responsible nurse',
    CONFLICT: 'Conflicting instructions — clinical review required',
  };
  const local = labels[state] ?? 'Currentness unknown — confirm with the responsible nurse';
  return cached ? `Saved earlier; remote changes unknown. ${local}` : local;
}
export type MealtimeDeliveryContext = {
  instruction_id: string | null;
  hash: string | null;
  as_of: string | null;
  currentness?: string;
  source?: 'live' | 'cached' | 'unavailable';
  outcome: 'FOLLOWED' | 'VARIATION' | 'REFUSED' | 'WITHHELD' | 'MISSED' | 'UNABLE_TO_VERIFY';
  reason?: string;
  acute_concern?: boolean;
};
export function displayedMealtimeContext(
  p?: MealtimeProjection | null,
  source: 'live' | 'cached' | 'unavailable' = 'live',
): MealtimeDeliveryContext {
  return {
    instruction_id: p?.profile?.id ?? null,
    hash: p?.profile?.hash ?? null,
    as_of: p?.as_of ?? null,
    currentness: p ? mealtimeCurrentness(p, Date.now(), source === 'cached') : 'READ_UNAVAILABLE',
    source: p ? source : 'unavailable',
    outcome: 'UNABLE_TO_VERIFY',
  };
}

export function mealtimeAllergySummary(p: MealtimeProjection): string {
  const known = p.allergies
    .filter((a) => a.status === 'active')
    .map((a) => `${a.allergen} (${a.reaction_type})`)
    .join('; ');
  if (p.allergy_state === 'UNAVAILABLE')
    return `Allergy read unavailable — confirm with the responsible nurse${known ? '; ' + known : ''}`;
  if (p.allergy_state === 'CONFLICT')
    return `Conflicting allergy and no-known-allergy evidence — review required${known ? '; ' + known : ''}`;
  if (known) return known;
  return p.allergy_state === 'RECORDED' &&
    p.allergies.some((a) => a.status === 'no_known_allergies')
    ? 'Explicit no-known-allergy evidence recorded'
    : 'Unknown — confirm in the allergy register';
}

export function hasAuthorisedMealtimePublication(
  p: MealtimeProjection | null | undefined,
  now = Date.now(),
): boolean {
  return Boolean(
    p?.profile?.id &&
    ['CURRENT_AUTHORISED', 'INTERIM', 'REVIEW_OVERDUE'].includes(p.state) &&
    (!p.profile.to || Date.parse(p.profile.to) > now) &&
    (!p.profile.expires_at || Date.parse(p.profile.expires_at) > now),
  );
}
